import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { DEFAULT_LOCALE } from "./locale";
import en from "./locales/en.json";
import enUS from "./locales/en-US.json";
import enCA from "./locales/en-CA.json";
import es from "./locales/es.json";

// One shared i18next instance for both apps. Using the default instance means
// react-i18next's useTranslation() works anywhere without an <I18nextProvider>.
// Resources are inline (no async loading), so init is synchronous at import time.
//
// en-US, en-CA and es fall back to `en` for any key they don't override — so a
// partly translated catalog still renders (English for missing keys) rather than
// showing raw key names. en-CA in particular is tiny (only -ize spellings), relying
// on the base for everything else. Language is driven from the persisted `locale`
// preference (see LocaleBootstrap in each app), not detected here.
if (!i18next.isInitialized) {
  i18next.use(initReactI18next).init({
    resources: {
      en: { translation: en },
      "en-US": { translation: enUS },
      "en-CA": { translation: enCA },
      es: { translation: es },
    },
    lng: DEFAULT_LOCALE,
    fallbackLng: { "en-US": ["en"], "en-CA": ["en"], es: ["en"], default: ["en"] },
    interpolation: { escapeValue: false }, // React already escapes output
    returnNull: false,
  });
}

export { i18next };

// The shape of the translate function returned by useT — also usable standalone.
export type TFunc = (key: string, options?: Record<string, unknown>) => string;

// Standalone translate bound to the shared instance's *current* language. For use
// in non-React code (formatters, share/export text) and as the default for
// formatters so callers that don't thread a `t` still localise. Components should
// prefer useT()'s `t` (it also re-renders them on a language switch).
export const translate: TFunc = (key, options) =>
  i18next.t(key, options ?? {}) as string;
